import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { apiDeleteStoryById } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';

type DeleteStoryDialogProps = {
  children: ReactNode;
  storyId: string;
};

export const DeleteStoryDialog = ({ children, storyId }: DeleteStoryDialogProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const deleteStoryMutation = useMutation<void, Error, string>({
    mutationFn: (id) => {
      return apiDeleteStoryById(String(id)).then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['stories'],
      });
    },
  });

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{intl.formatMessage({ id: 'dialog.confirmAction' })}</DialogTitle>
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => deleteStoryMutation.mutate(storyId)}
          >
            {intl.formatMessage({ id: 'button.delete' })}
          </Button>

          <DialogClose asChild>
            <Button variant="secondary">
              {intl.formatMessage({ id: 'button.close' })}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
